import React, { useState } from 'react';
import { X, ShoppingCart } from 'lucide-react';
import PropTypes from 'prop-types';

const AssetDetailView = ({ asset, onClose, onAddToCart }) => {
  const [currentImage, setCurrentImage] = useState(asset.ProfilePicture || 'CartNoImage.png');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-2xl font-semibold text-gray-800">Asset Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Images */}
            <div className="space-y-4">
              <div className="w-full h-[400px] border border-gray-200 rounded-lg overflow-hidden">
                <img
                  src={`${process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL}${currentImage}`}
                  alt={asset.Name}
                  className="w-full h-full object-contain"
                />
              </div>
              
              {asset.Pictures && asset.Pictures.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  <div
                    onClick={() => setCurrentImage(asset.ProfilePicture)}
                    className={`w-20 h-20 border rounded-md cursor-pointer p-1 ${
                      currentImage === asset.ProfilePicture ? 'border-blue-500' : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={`${process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL}${asset.ProfilePicture}`}
                      alt="main"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  {asset.Pictures.map((img, index) => (
                    <div
                      key={index}
                      onClick={() => setCurrentImage(img.fileName)}
                      className={`w-20 h-20 border rounded-md cursor-pointer p-1 ${
                        currentImage === img.fileName ? 'border-blue-500' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={`${process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL}${img.fileName}`}
                        alt={`preview ${index + 1}`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="space-y-6">
              <div>
                <span className="text-sm font-medium text-gray-500 uppercase">
                  {asset.AssetTypeName}
                </span>
                <h2 className="text-2xl font-bold text-gray-900 mt-1">
                  {asset.Name}
                </h2>
                <p className="text-xl font-semibold text-blue-600 mt-2">
                  {asset.UnitPriceText}
                </p>
              </div>

              {asset.Description && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                  <div 
                    className="text-gray-600 prose"
                    dangerouslySetInnerHTML={{ __html: asset.Description }}
                  />
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Details</h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Asset Group</dt>
                    <dd className="text-sm text-gray-900">{asset.AssetGroupName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Asset Code</dt>
                    <dd className="text-sm text-gray-900">{asset.Code}</dd>
                  </div>
                  {asset.Properties && asset.Properties.length > 0 && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 mb-1">Properties</dt>
                      <dd className="text-sm text-gray-900">
                        <ul className="list-disc list-inside space-y-1">
                          {asset.Properties.map((prop, index) => (
                            <li key={index}>
                              {prop.name}: {prop.value}
                            </li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Availability and Add to Cart */}
              <div className="pt-6 border-t">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-500">Availability</span>
                  {asset.AvailableCount > 0 ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      {asset.AvailableCount} Available
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                      Not Available
                    </span>
                  )}
                </div>
                
                {asset.AvailableCount > 0 && (
                  <button
                    onClick={() => onAddToCart(asset)}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ShoppingCart className="h-5 w-5" />
                    <span>Add to Cart</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AssetDetailView.propTypes = {
  asset: PropTypes.shape({
    Id: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
    Code: PropTypes.string.isRequired,
    Description: PropTypes.string,
    ProfilePicture: PropTypes.string,
    AssetTypeName: PropTypes.string,
    AssetGroupName: PropTypes.string,
    UnitPriceText: PropTypes.string,
    AvailableCount: PropTypes.number,
    Properties: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string
      })
    ),
    Pictures: PropTypes.arrayOf(
      PropTypes.shape({
        fileName: PropTypes.string,
      })
    )
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onAddToCart: PropTypes.func.isRequired,
};

export default AssetDetailView;